import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, MenuItem, Tooltip, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { Fragment, React, useEffect, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import AlertCircleIco from "../../../../assets/icons/AlertCircleIco";
import CommonButton from "../../../../component/Button";
import CommonTextField from "../../../../component/Input";
import DropDownSelect from "../../../../component/Select";
import {
  getIconForKeyColors,
  HandleSkeletion,
} from "../../../../utils/constants";
import { CustomDiv } from "../../../chat-bot/style";
import {
  CommonGrid2,
  CustomTooltip,
  DotColor,
} from "../../../common-components/StyledComponents";
import { CustomizeSpan, CustomTooltipField } from "../../style";

export default function Skill({
  alignment,
  handleAddNewSkill,
  highPriority,
  lowPriority,
  mediumPriority,
  recommended,
  jobModifyData,
  handleSkillDropDownChange,
  handleSkillTextChange,
  handleDeleteSkillRow,
  iconVisible,
  handleSkillDrag,
  initialized,
  isLoading,
}) {
  const { values, errors } = useFormikContext();
  const debounceTimer = useRef(null);
  const [skillSections, setSkillSections] = useState([
    {
      title: "Core Skills",
      fieldName: "high_priority",
      rowData: highPriority,
      scoreFieldName: "score_hard_skills",
      high_priority: values?.["skillData"]?.["high_priority"],
    },
    {
      title: "Complementary Skills",
      fieldName: "low_priority",
      rowData: lowPriority,
      scoreFieldName: "score_soft_skills",
      low_priority: values?.["skillData"]?.["low_priority"],
    },
    {
      title: "Recommended based on Historical/Empirical data",
      fieldName: "recommended",
      rowData: recommended,
      scoreFieldName: "score_soft_skills",
      recommended: values?.["skillData"]?.["recommended"],
    },
    {
      title: "Medium Priority",
      fieldName: "medium_priority",
      rowData: mediumPriority,
      scoreFieldName: "score_soft_skills",
      medium_priority: values?.["skillData"]?.["medium_priority"],
    },
  ]);

  useEffect(() => {
    if (!initialized.current)
      setSkillSections([
        {
          title: "Core Skills",
          fieldName: "high_priority",
          rowData: highPriority,
          scoreFieldName: "score_hard_skills",
          high_priority: values?.skillData?.high_priority,
        },
        {
          title: "Complementary Skills",
          fieldName: "low_priority",
          rowData: lowPriority,
          scoreFieldName: "score_soft_skills",
          low_priority: values?.skillData?.low_priority,
        },
        {
          title: "Recommended based on Historical/Empirical data",
          fieldName: "recommended",
          rowData: recommended,
          scoreFieldName: "score_soft_skills",
          recommended: values?.skillData?.recommended,
        },
        {
          title: "Medium Priority",
          fieldName: "medium_priority",
          rowData: mediumPriority,
          scoreFieldName: "score_soft_skills",
          medium_priority: values?.skillData?.medium_priority,
        },
      ]);
  }, [values]);

  const handleSkillText = (event, index, name, fieldName) => {
    const getData = event.target.value;
    initialized.current = true;
    setSkillSections((prev) =>
      prev.map((section) => {
        if (section.fieldName !== name) return section;

        const updatedFieldData = section[name]?.map((row, rowIndex) => {
          if (rowIndex === index) {
            return { ...row, [fieldName]: getData };
          }
          return row;
        });

        return {
          ...section,
          [name]: updatedFieldData,
        };
      })
    );

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      await handleSkillTextChange(event, index, values, name, fieldName);
      initialized.current = false;
    }, 500);
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    const sourceSectionIndex = skillSections.findIndex(
      (section) => section.fieldName === source.droppableId
    );
    const destinationSectionIndex = skillSections.findIndex(
      (section) => section.fieldName === destination.droppableId
    );

    if (sourceSectionIndex === -1 || destinationSectionIndex === -1) return;

    const newSections = [...skillSections];
    const sourceKey = newSections[sourceSectionIndex].fieldName;
    const destinationKey = newSections[destinationSectionIndex].fieldName;

    const sourceItems = [...(newSections[sourceSectionIndex][sourceKey] || [])];
    const destinationItems =
      source.droppableId === destination.droppableId
        ? sourceItems
        : [...(newSections[destinationSectionIndex][destinationKey] || [])];

    const [movedItem] = sourceItems.splice(source.index, 1);

    const destinationPriority = jobModifyData.priorityData.find(
      (p) => p.key === destinationKey
    );
    if (destinationPriority) {
      movedItem.bucket = destinationPriority.value;
    }

    destinationItems.splice(destination.index, 0, movedItem);

    newSections[sourceSectionIndex][sourceKey] = sourceItems;
    newSections[destinationSectionIndex][destinationKey] = destinationItems;

    setSkillSections(newSections);

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      handleSkillDrag(result, sourceItems, destinationItems);
    }, 500);
  };

  const CustomMenuItem = (item, index) => (
    <MenuItem key={index} value={item.value} name={item.name}>
      {item.name}
    </MenuItem>
  );

  return alignment === "View" ? (
    <CommonGrid2
      spacing={2}
      container
      overflowX={"auto"}
      flexWrap="nowrap"
      padding={"0px 0px 1rem 0px"}
      scrollStyle
      scrollBar="6px"
    >
      {skillSections.map((item) => {
        return (
          item.rowData &&
          item.rowData?.length !== 0 && (
            <CommonGrid2
              size={{ xs: 12, md: 6, sm: 6, lg: 4 }}
              scrollStyle
              scrollBar="6px"
              sx={{
                overflow: {
                  xs: "unset",
                  sm: "auto",
                  md: "auto",
                  lg: "unset",
                },
                maxHeight: {
                  sm: 600,
                  md: 600,
                },
                minWidth: {
                  sm: 300,
                  md: 300,
                },
              }}
            >
              <CommonGrid2
                container
                justifyContent={"space-between"}
                alignItems={"center"}
                marginBottom={"1rem"}
              >
                <Typography variant="h6">{item.title}</Typography>
              </CommonGrid2>
              <CommonGrid2
                background="#F5F6F7"
                container
                spacing={1.5}
                flexDirection={"column"}
                padding={"0.8rem"}
                borderRadius={"10px"}
              >
                {item.rowData.map((list, getlist) => (
                  <Fragment>
                    {isLoading ? (
                      <HandleSkeletion
                        margin="5px 0px"
                        height={65}
                        transform="scale(1)"
                      />
                    ) : (
                      <CustomDiv
                        background="#fff"
                        borderRadius="10px"
                        border="1px solid #EAECF0"
                        padding="10px 12px"
                      >
                        <CommonGrid2
                          container
                          justifyContent={"space-between"}
                          alignItems={"center"}
                        >
                          <Typography
                            variant="p"
                            sx={{
                              cursor: list?.about_the_skill
                                ? "pointer"
                                : "default",
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                            fontSize={"14px"}
                            fontWeight={600}
                          >
                            <Tooltip
                              disableHoverListener={!list?.about_the_skill}
                              title={
                                <CommonGrid2
                                  display={"flex"}
                                  justifyContent={"center"}
                                  alignItems={"center"}
                                  textAlign={"center"}
                                  fontWeight={600}
                                >
                                  {list?.about_the_skill}
                                </CommonGrid2>
                              }
                              arrow
                              placement="top"
                            >
                              <CustomizeSpan>{list.skill}</CustomizeSpan>
                            </Tooltip>
                            {(list?.is_new || iconVisible) && (
                              <CommonGrid2
                                gap={"10px"}
                                display={"flex"}
                                flexWrap={"nowrap"}
                                minWidth={"auto"}
                                justifyContent={"end"}
                              >
                                {list?.is_new && (
                                  <CustomTooltip
                                    title={list?.recency_reasoning}
                                    placement="top"
                                    fontWeight={700}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      color="#7F56D9"
                                      backgroundColor={"#EBE1F9"}
                                      padding={"1px 10px"}
                                      fontWeight={600}
                                      borderRadius={"50px"}
                                    >
                                      New
                                    </Typography>
                                  </CustomTooltip>
                                )}
                                {iconVisible &&
                                  getIconForKeyColors(
                                    list?.key_colors,
                                    list?.skill,
                                    list?.expertise_level,
                                    list?.experience_in_yrs,
                                    list?.about_the_skill
                                  )}
                              </CommonGrid2>
                            )}
                          </Typography>
                        </CommonGrid2>

                        <CommonGrid2
                          display={"flex"}
                          flexWrap={"wrap"}
                          justifyContent={"space-between"}
                          alignItems={"end"}
                        >
                          <CommonGrid2
                            display={"flex"}
                            gap={"4px"}
                            marginTop={"1rem"}
                          >
                            <Typography
                              variant="p"
                              fontWeight={600}
                              color="#5B738B"
                            >
                              {list.expertise_level}
                            </Typography>
                            -
                            <Typography
                              variant="p"
                              fontWeight={600}
                              color="#5B738B"
                            >
                              {list.experience_in_yrs} Yrs
                            </Typography>
                          </CommonGrid2>
                          {list?.skill_anomoly !== "" &&
                            list?.skill_anomoly !== undefined && (
                              <Tooltip
                                title={
                                  <CommonGrid2
                                    display={"flex"}
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                    textAlign={"center"}
                                  >
                                    {list?.skill_anomoly}
                                  </CommonGrid2>
                                }
                                arrow
                                placement="top"
                              >
                                <CustomizeSpan cursor="pointer">
                                  <AlertCircleIco />
                                </CustomizeSpan>
                              </Tooltip>
                            )}
                        </CommonGrid2>
                      </CustomDiv>
                    )}
                  </Fragment>
                ))}
              </CommonGrid2>
            </CommonGrid2>
          )
        );
      })}
    </CommonGrid2>
  ) : (
    <DragDropContext onDragEnd={handleDragEnd}>
      <CommonGrid2
        spacing={2}
        container
        overflowX={"auto"}
        flexWrap="nowrap"
        padding={"0px 0px 1rem 0px"}
      >
        {skillSections.map((item, index) => {
          return (
            item.rowData &&
            item.rowData?.length !== 0 && (
              <Droppable droppableId={item.fieldName} key={item.fieldName}>
                {(provided) => (
                  <CommonGrid2
                    size={{ xs: 12, md: 6, sm: 6, lg: 4 }}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    // scrollStyle
                    // scrollBar="6px"
                    sx={{
                      //   overflow: {
                      //     xs: "unset",
                      //     sm: "auto",
                      //     md: "auto",
                      //     lg: "unset",
                      //   },
                      //   maxHeight: {
                      //     sm: 600,
                      //     md: 600,
                      //   },
                      minWidth: {
                        sm: 280,
                        md: 280,
                      },
                    }}
                  >
                    <CommonGrid2
                      container
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      marginBottom={"1rem"}
                      width={"100%"}
                    >
                      <Typography variant="h6">{item.title}</Typography>
                      <CommonButton
                        value={"Add"}
                        variant="text"
                        startIcon={<AddIcon />}
                        gap="0px"
                        padding={"5px 15px"}
                        fontWeight={600}
                        borderRadius={"5px"}
                        onClick={() => handleAddNewSkill(values)}
                      />
                    </CommonGrid2>

                    <CommonGrid2
                      background="#F5F6F7"
                      container
                      spacing={1.5}
                      flexDirection={"column"}
                      padding={"0.8rem"}
                      borderRadius={"10px"}
                    >
                      {(item[item.fieldName] || []).map((list, listIndex) => (
                        <Draggable
                          key={`${item.fieldName}-${list.skill}`}
                          draggableId={`${item.fieldName}-${list.skill}`}
                          index={listIndex}
                        >
                          {(provided, snapshot) => (
                            <Fragment>
                              {isLoading ? (
                                <HandleSkeletion
                                  margin="5px 0px"
                                  height={65}
                                  transform="scale(1)"
                                />
                              ) : (
                                <CustomDiv
                                  background="#fff"
                                  borderRadius="10px"
                                  border="1px solid #EAECF0"
                                  padding="10px"
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    ...provided.draggableProps.style,
                                    boxShadow: snapshot.isDragging
                                      ? "0px 4px 6px rgba(0,0,0,0.1)"
                                      : "none",
                                  }}
                                >
                                  <CommonGrid2
                                    container
                                    justifyContent={"space-between"}
                                    alignItems={"center"}
                                    flexWrap={"nowrap"}
                                  >
                                    <Typography
                                      variant="p"
                                      fontSize={"14px"}
                                      fontWeight={600}
                                      sx={{
                                        cursor: list?.about_the_skill
                                          ? "pointer"
                                          : "default",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        justifyContent: "space-between",
                                        width: "100%",
                                      }}
                                    >
                                      <Tooltip
                                        disableHoverListener={
                                          !list?.about_the_skill
                                        }
                                        title={
                                          <CommonGrid2
                                            display={"flex"}
                                            justifyContent={"center"}
                                            alignItems={"center"}
                                            textAlign={"center"}
                                            fontWeight={600}
                                          >
                                            {list?.about_the_skill}
                                          </CommonGrid2>
                                        }
                                        arrow
                                        placement="top"
                                      >
                                        <CustomizeSpan>
                                          {list.skill}
                                        </CustomizeSpan>
                                      </Tooltip>

                                      {(list?.is_new || iconVisible) && (
                                        <CommonGrid2
                                          gap={"10px"}
                                          display={"flex"}
                                          flexWrap={"nowrap"}
                                          minWidth={"auto"}
                                          justifyContent={"end"}
                                        >
                                          {list?.is_new && (
                                            <Typography
                                              variant="subtitle2"
                                              color="#7F56D9"
                                              backgroundColor={"#EBE1F9"}
                                              padding={"1px 10px"}
                                              fontWeight={600}
                                              borderRadius={"50px"}
                                            >
                                              New
                                            </Typography>
                                          )}
                                          {iconVisible &&
                                            getIconForKeyColors(
                                              list?.key_colors,
                                              list?.skill,
                                              list?.expertise_level,
                                              list?.experience_in_yrs,
                                              list?.about_the_skill
                                            )}
                                        </CommonGrid2>
                                      )}
                                    </Typography>
                                  </CommonGrid2>
                                  <CommonGrid2
                                    container
                                    justifyContent={"space-between"}
                                    alignItems={"end"}
                                    marginTop={"1rem"}
                                    size={12}
                                  >
                                    <CommonGrid2
                                      display={"flex"}
                                      flexDirection={"column"}
                                      gap={"4px"}
                                      size={6}
                                    >
                                      <Typography
                                        variant="p"
                                        fontSize={"12px"}
                                        fontWeight={600}
                                      >
                                        Criticality
                                      </Typography>
                                      <DropDownSelect
                                        marginTop="0px"
                                        sizeMedium="-12px"
                                        removeAnimation={true}
                                        data={
                                          list?.type ===
                                            "relevant_hard_skill" ||
                                          list?.type ===
                                            "extracted_hard_skill" ||
                                          list?.type === "user_added_hard_skill"
                                            ? jobModifyData.criticalityHardData
                                            : jobModifyData.criticalitySoftData
                                        }
                                        valueChange={true}
                                        fullWidth={false}
                                        title={"Select Criticality"}
                                        CustomMenuItem={CustomMenuItem}
                                        handleRenderValue={(selected) => {
                                          const data =
                                            list?.type ===
                                              "relevant_hard_skill" ||
                                            list?.type ===
                                              "extracted_hard_skill" ||
                                            list?.type ===
                                              "user_added_hard_skill"
                                              ? jobModifyData.criticalityHardData
                                              : jobModifyData.criticalitySoftData;
                                          const selectedOption = data.find(
                                            (option) =>
                                              option.value === selected
                                          );
                                          return (
                                            <Box
                                              display={"flex"}
                                              alignItems={"center"}
                                            >
                                              <DotColor
                                                color={selectedOption?.color}
                                                marginRight="8px"
                                              />
                                              <Typography>
                                                {selectedOption?.name}
                                              </Typography>
                                            </Box>
                                          );
                                        }}
                                        customStyle={{
                                          "& .MuiSelect-select": {
                                            padding:
                                              "5px 32px 5px 10px !important",
                                            borderRadius: "5px",
                                          },

                                          "& .MuiSelect-icon": {
                                            right: "0rem",
                                          },
                                        }}
                                        width={"115px"}
                                        maxHeight={"215px"}
                                        value={list.expertise_level}
                                        handleChange={(e) =>
                                          handleSkillDropDownChange(
                                            e,
                                            listIndex,
                                            values,
                                            item.fieldName
                                          )
                                        }
                                      />
                                    </CommonGrid2>

                                    <CommonGrid2
                                      display={"flex"}
                                      flexDirection={"column"}
                                      gap={"4px"}
                                      size={4}
                                    >
                                      <CustomDiv position="relative">
                                        <Typography
                                          variant="p"
                                          fontSize={"12px"}
                                          fontWeight={600}
                                        >
                                          Years
                                        </Typography>

                                        <CommonTextField
                                          placeholder={"Year"}
                                          padding="5px 10px"
                                          borderRadius="5px"
                                          placeholderFontSize={"14px"}
                                          paddingRight="0px"
                                          fontSize={"14px"}
                                          value={list.experience_in_yrs}
                                          onChange={(event) =>
                                            handleSkillText(
                                              event,
                                              listIndex,
                                              item.fieldName,
                                              "experience_in_yrs"
                                            )
                                          }
                                        />
                                        {errors?.skillData?.[item.fieldName]?.[
                                          listIndex
                                        ]?.["experience_in_yrs"] && (
                                          <CustomTooltipField
                                            size={7}
                                            top="-22px"
                                            className="Mui-error"
                                          >
                                            {
                                              errors?.skillData?.[
                                                item.fieldName
                                              ]?.[listIndex]?.[
                                                "experience_in_yrs"
                                              ]
                                            }
                                          </CustomTooltipField>
                                        )}
                                      </CustomDiv>
                                    </CommonGrid2>

                                    <CommonGrid2
                                      size={1}
                                      display={"flex"}
                                      flexDirection={"column"}
                                      justifyContent={"end"}
                                    >
                                      {list?.skill_anomoly !== "" &&
                                        list?.skill_anomoly !== undefined && (
                                          <Tooltip
                                            title={
                                              <CommonGrid2
                                                display={"flex"}
                                                justifyContent={"center"}
                                                alignItems={"center"}
                                                textAlign={"center"}
                                              >
                                                {list?.skill_anomoly}
                                              </CommonGrid2>
                                            }
                                            arrow
                                            placement="top"
                                          >
                                            <CustomizeSpan cursor="pointer">
                                              <AlertCircleIco />
                                            </CustomizeSpan>
                                          </Tooltip>
                                        )}
                                      <CustomTooltip
                                        title="Delete"
                                        backgroundColor="#000"
                                      >
                                        <DeleteIcon
                                          style={{
                                            cursor: "pointer",
                                            width: "22px",
                                          }}
                                          onClick={() =>
                                            handleDeleteSkillRow(
                                              list,
                                              values,
                                              item.fieldName
                                            )
                                          }
                                        />
                                      </CustomTooltip>
                                    </CommonGrid2>
                                  </CommonGrid2>
                                </CustomDiv>
                              )}
                            </Fragment>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </CommonGrid2>
                  </CommonGrid2>
                )}
              </Droppable>
            )
          );
        })}
      </CommonGrid2>
    </DragDropContext>
  );
}
